import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import toast from "react-hot-toast";

import SvgLoadingAnimation from "../../components/loading";
import Button from "../../components/button/Button";
import ConfirmModal from "../../components/modal/ConfirmModal";
import { getAuthTokenFromCookies } from "../../common/utils";
import {
  useGetNotesTreeQuery,
  useGetNoteQuery,
  useLazyGetNoteQuery,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useGetNoteChangeQuery,
  useGetNoteChangesQuery,
  useCreateNoteChangeMutation,
  useUpdateNoteChangeMutation,
  useDeleteNoteChangeMutation,
  type NoteChangePayload,
  type NoteChangeTargetType,
  type NoteDetail,
  type NoteContentStyle,
  type NoteFolderSummary,
  type NoteSection,
  type NoteSummary,
  toContentStyle,
} from "../../app/services/notes";
import { useIsTwoColumn } from "../../hooks/useResponsiveLayout";
import useScrollDirectionLayoutSync from "../../hooks/useScrollDirectionLayoutSync";
import NotesSidebar from "./components/NotesSidebar";
import NoteView from "./components/NoteView";
import NoteEditor, { type NoteSavePayload } from "./components/NoteEditor";
import NoteCardView from "./components/NoteCardView";
import ChangesListView from "./components/ChangesListView";
import ChangeDetailView from "./components/ChangeDetailView";
import { toChangePayload } from "./components/ChangeEditModal";
import SubmitChangeModal from "./components/SubmitChangeModal";
import SectionSettingsModal from "./components/SectionSettingsModal";
import ImportNotesModal from "./components/ImportNotesModal";
import ImageBoard from "../imageboard/ImageBoard";
// zip 同梱対象の判定とファイル名復元は添付カード表示（MarkdownContent）と同じ
// 規則を使う。fileNameFromUrl は S3 の一意化サフィックスも除去するため、
// zip 内の添付も元のファイル名になる（重複は uniqueAssetFileName が採番）。
import {
  fileNameFromUrl,
  isNoteUploadUrl,
} from "../../components/markdown/markdownAttachment";
import {
  downloadBlob,
  extractAttachmentUrls,
  extractImageUrls,
  rewriteFileRefs,
  sanitizeFileName,
  uniqueAssetFileName,
  uniqueFileName,
} from "./utils/export";
import { fetchFileAsBlob } from "../../utils/downloadFile";

/**
 * ノート（Notes）ページ。
 *
 * Notion / Obsidian 風の Markdown 情報集約ツール。全体で 1 つのノートスペースを
 * 「セクション > ノート」の 2 階層で構成し、左サイドバー（目次ツリー）+
 * 本文表示 / WYSIWYG エディタ（NoteEditor）で閲覧・編集する。
 *
 * 2 つの表示モードを持つ:
 * - スタンドアロン（グローバルメニューの「共有ノート」）: `/notes` /
 *   `/notes/:noteId`。参加権限のある全セクションを横断表示する。
 *   `?project=<id>` を付けるとプロジェクト絞り込みの直リンクにもなる。
 *   `?section=<id>` は単一セクションの絞り込み（マイページ「ヘルプセンター」
 *   からの遷移で、割当セクションだけを見せる用途）。
 * - プロジェクト内埋め込み（`embedded`）: ProjectWrapper の notes タブ
 *   （`/:projectId?tab=notes`）としてプロジェクトバナーの下に表示する。
 *
 * OSS 型ワークフロー:
 * - 権限はセクション単位のロール（オーナー / メンテナー / コントリビューター）
 * - 公開データの更新は原則プルリクエスト（変更提案）経由。直接更新
 *   （can_direct_update）は「承認者が自分だけ」の場合と、承認不要フォルダ
 *   （effective_skip_review）の中だけ
 * - `?changes=<sectionId>` でセクションの PR 一覧、`?change=<changeId>` で
 *   PR 詳細を表示する（メール通知のディープリンク先）
 */

/**
 * ページ外枠。高さを確定させて自身は overflow: hidden とし、スクロールは
 * サイドバー / 本文それぞれの内側（overflow-y: auto）で独立して行う
 * （DM / 設定画面の 2 カラムと同じ構造）。
 * - スタンドアロン: GlobalHeader の表示状態（state.layout）に合わせて
 *   100dvh からヘッダー分を差し引く（GlobalWrapper の padding-top と連動）
 * - 埋め込み: ProjectWrapper の PanelTab（$fill）いっぱいに広がる
 * - 1 カラム時は MobileToolbar + （サイドバー or 本文）の縦積み
 */
const PageWrapper = styled.div<{
  $embedded: boolean;
  $singleColumn: boolean;
  $scrollDown: boolean;
}>`
  display: flex;
  flex-direction: ${({ $singleColumn }) =>
    $singleColumn ? "column" : "row"};
  width: 100%;
  height: ${({ $embedded, $scrollDown }) =>
    $embedded
      ? "100%"
      : $scrollDown
        ? "100dvh"
        : "calc(100dvh - var(--global-header-height))"};
  transition: height 0.15s linear;
  align-items: stretch;
  overflow: hidden;
`;

/** PC 用サイドバー外枠。幅はドラッグ境界（ResizeDivider）で可変 */
const SidebarPane = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  flex-shrink: 0;
  height: 100%;
  min-height: 0;
`;

/**
 * 1 カラム時にツールバー下の残り高さを占めるサイドバー用の枠。
 * 高さを確定させることで NotesSidebar（height: 100% + overflow-y: auto）が
 * この中で独立してスクロールする。
 */
const SingleColumnSidebarPane = styled.div`
  flex: 1;
  min-height: 0;
`;

/** ドラッグ可能な境界線（Preview.tsx のコメントパネルと同じパターン） */
const ResizeDivider = styled.div<{ $isDragging: boolean }>`
  width: 6px;
  flex-shrink: 0;
  cursor: col-resize;
  background: ${({ $isDragging }) =>
    $isDragging ? "var(--soremo-blue)" : "var(--soremo-border)"};
  transition: background 0.15s;
  position: relative;
  z-index: 5;

  &:hover {
    background: rgba(0, 154, 206, 0.4);
  }
`;

const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 480;

/**
 * 本文エリア。PageWrapper（高さ確定 + overflow: hidden）の中で
 * 自身が overflow-y: auto の独立スクロールコンテナになる。
 * $padTop: 埋め込み 2 カラム時はパネル右上にプロジェクトメニューがフロートする
 * ため、NoteView ヘッダーの編集 / 削除ボタンと重ならないよう上に余白を取る
 * （1 カラム時は MobileToolbar 行がありアイコンはその右の空きに収まる）。
 * $hidden: 1 カラムでサイドバー表示中は display: none で隠す（アンマウント
 * するとノート編集中の内容が失われるため、マウントは維持する）。
 */
const ContentArea = styled.main<{
  $padTop: boolean;
  $hidden: boolean;
  $board: boolean;
}>`
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  /* コンテンツ量の増減でスクロールバーが出入りしても幅が変わらないように */
  scrollbar-gutter: stable;
  padding-top: ${({ $padTop }) => ($padTop ? "48px" : "0")};
  /* イメージボードはキャンバスを本文エリアいっぱいに広げ、中でパン / ズームする
   * （スクロールしないためガターの確保も外す） */
  ${({ $board }) =>
    $board
      ? "display: flex; flex-direction: column; overflow: hidden; scrollbar-gutter: auto;"
      : ""}
  ${({ $hidden }) => ($hidden ? "display: none;" : "")}
`;

/** 1 カラム時のみ描画するサイドバー開閉ツールバー（描画判定は JS 側） */
const MobileToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--soremo-border);
  flex-shrink: 0;
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--soremo-gray);
  padding: 4px;

  .material-symbols-rounded {
    font-size: 20px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 96px 16px;
  text-align: center;
`;

/** 下書き編集中バナー（エディタ上部） */
const DraftNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(0, 154, 206, 0.3);
  background: rgba(0, 154, 206, 0.06);
  color: var(--soremo-blue);

  .material-symbols-rounded {
    font-size: 18px;
  }
`;

/**
 * Markdown 一括書き出し用に、フォルダ ID → zip 内ディレクトリパスの
 * 対応表を作る。入れ子フォルダは `親/子` のサブディレクトリで再現し、
 * 同一階層の同名フォルダは uniqueFileName で採番する。
 *
 * base を指定するとそのフォルダを基準（zip 直下 = null）とした相対パスにし、
 * base の子孫のみを対象にする（それ以外のフォルダは map に含めない）。
 */
const buildFolderDirMap = (
  folders: NoteFolderSummary[],
  base?: NoteFolderSummary,
): Map<string, string | null> => {
  const ids = new Set(folders.map((f) => f.id));
  const byParent = new Map<string | null, NoteFolderSummary[]>();
  for (const f of folders) {
    const parent = f.parent_id && ids.has(f.parent_id) ? f.parent_id : null;
    const list = byParent.get(parent) ?? [];
    list.push(f);
    byParent.set(parent, list);
  }
  const dirs = new Map<string, string | null>();
  const walk = (parentId: string | null, parentPath: string | null) => {
    const used = new Set<string>();
    for (const f of byParent.get(parentId) ?? []) {
      const name = uniqueFileName(
        sanitizeFileName(f.title || "フォルダ"),
        used,
      );
      const path = parentPath ? `${parentPath}/${name}` : name;
      dirs.set(f.id, path);
      walk(f.id, path);
    }
  };
  if (base) {
    dirs.set(base.id, null);
    walk(base.id, null);
  } else {
    walk(null, null);
  }
  return dirs;
};

type EditorState =
  | { mode: "closed" }
  | { mode: "edit" }
  | {
      mode: "create";
      section: NoteSection;
      /** フォルダの3点メニューからの作成時、そのフォルダ直下に置く */
      folder?: NoteFolderSummary;
    };

/** プルリク送信モーダルに引き渡す保留中の変更内容 */
interface PendingSubmit {
  targetType: NoteChangeTargetType;
  targetId?: string;
  sectionId: string;
  payload: NoteChangePayload;
  /** 既存下書きを送信する場合はその ID */
  draftId?: string;
  /** 既存下書きの説明コメント（送信モーダルの初期値） */
  draftDescription?: string;
}

interface NotesProps {
  /** ProjectWrapper の notes タブ（`?tab=notes`）としての埋め込み表示 */
  embedded?: boolean;
}

const Notes: React.FC<NotesProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { noteId: routeNoteId, projectId } = useParams<{
    noteId: string;
    projectId: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  // レイアウト分岐（1 カラム ⇄ 2 カラム）はビューポート幅ベースの共通判定。
  // UA ベースの isMobile だと iPad で「サイドバーも開閉ボタンも出ない」
  // デッドゾーンが生まれるため使わない
  const isTwoColumn = useIsTwoColumn();
  // GlobalHeader の表示状態（"down" = 非表示）。スタンドアロン時の高さ計算用
  const scrollState = useSelector((state: { layout: string }) => state.layout);
  // 内側スクロールから GlobalHeader / BottomAppBar の表示切替へ連動させる
  // （DM の MessengerContent と同じフェイルセーフ）
  const contentScrollSync = useScrollDirectionLayoutSync<HTMLElement>();

  // 旧カード URL（?card=）互換: カードはノートに統合されたため、
  // ?card=<id> はノート ID として解釈する（後段の effect で URL も正規化）
  const legacyCardId = searchParams.get("card") ?? undefined;

  // URL 上の選択中ノート: スタンドアロンはパス（/notes/:noteId）、
  // 埋め込みは ?note=
  const urlNoteId = embedded
    ? (searchParams.get("note") ?? legacyCardId)
    : (routeNoteId ?? legacyCardId);

  // プルリクエスト表示: ?change=<id>（詳細） / ?changes=<sectionId>（一覧）。
  // どちらもノート/カード選択より優先される。
  const changeId = searchParams.get("change") ?? undefined;
  const changesSectionId = searchParams.get("changes") ?? undefined;
  // 自分の下書き（未提出の変更提案）の通常ページ表示: ?draft=<changeId>。
  // ツリーの「下書き」行から開き、ノートと同じ見た目で閲覧・編集する
  const draftChangeId = searchParams.get("draft") ?? undefined;

  // 埋め込みで ?tab=notes が外れた直後（タブ切替の URL 反映はタブ表示より
  // 遅れる）も直前のノートを表示し続ける。ラッチしないと本文が一瞬
  // EmptyState に変わる。
  const isNotesTabActive = searchParams.get("tab") === "notes";
  const lastNoteIdRef = useRef(urlNoteId);
  if (urlNoteId) lastNoteIdRef.current = urlNoteId;
  const noteId =
    embedded && !isNotesTabActive
      ? (urlNoteId ?? lastNoteIdRef.current)
      : urlNoteId;

  // プロジェクト絞り込み: 埋め込みは現在のプロジェクト固定、
  // スタンドアロンは ?project= 直リンク指定時のみ
  const projectFilter = embedded
    ? (projectId ?? null)
    : searchParams.get("project");
  // セクション絞り込み: マイページ「ヘルプセンター」等からの直リンク
  // （?section=<id>）で、指定セクションのみをツリーに表示する
  const sectionFilter = embedded ? null : searchParams.get("section");
  // スタンドアロンのノート間遷移で絞り込み状態を保持するためのクエリサフィックス
  const filterSuffix = useMemo(() => {
    const params = new URLSearchParams();
    if (projectFilter) params.set("project", projectFilter);
    if (sectionFilter) params.set("section", sectionFilter);
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [projectFilter, sectionFilter]);

  /** 埋め込みモードのクエリ更新ヘルパー（tab=notes を維持しつつ選択を切替） */
  const setEmbeddedSelection = useCallback(
    (
      updates: Record<string, string | null>,
      options?: { replace?: boolean },
    ) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", "notes");
          for (const [key, value] of Object.entries(updates)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: options?.replace, state: location.state },
      );
    },
    [setSearchParams, location.state],
  );

  /** スタンドアロンモードのクエリ遷移ヘルパー */
  const navigateStandalone = useCallback(
    (
      path: string,
      params: Record<string, string>,
      options?: { replace?: boolean },
    ) => {
      const search = new URLSearchParams();
      if (projectFilter) search.set("project", projectFilter);
      if (sectionFilter) search.set("section", sectionFilter);
      for (const [key, value] of Object.entries(params)) {
        search.set(key, value);
      }
      const qs = search.toString();
      navigate(`${path}${qs ? `?${qs}` : ""}`, { replace: options?.replace });
    },
    [navigate, projectFilter, sectionFilter],
  );

  // ノート選択の遷移
  const goToNote = useCallback(
    (id: string, options?: { replace?: boolean }) => {
      if (embedded) {
        setEmbeddedSelection(
          { note: id, card: null, change: null, changes: null, draft: null },
          options,
        );
      } else {
        navigate(`/notes/${id}${filterSuffix}`, {
          replace: options?.replace,
        });
      }
    },
    [embedded, setEmbeddedSelection, navigate, filterSuffix],
  );

  // 旧カード URL（?card=）はノート URL へ正規化する（リンク互換のため）
  useEffect(() => {
    if (legacyCardId) goToNote(legacyCardId, { replace: true });
  }, [legacyCardId, goToNote]);

  // プルリクエスト一覧（セクション単位）への遷移
  const goToChangesList = useCallback(
    (sectionId: string) => {
      if (embedded) {
        setEmbeddedSelection({
          changes: sectionId,
          change: null,
          note: null,
          card: null,
          draft: null,
        });
      } else {
        navigateStandalone("/notes", { changes: sectionId });
      }
    },
    [embedded, setEmbeddedSelection, navigateStandalone],
  );

  // プルリクエスト詳細への遷移
  const goToChange = useCallback(
    (id: string, options?: { replace?: boolean }) => {
      if (embedded) {
        setEmbeddedSelection(
          {
            change: id,
            changes: null,
            note: null,
            card: null,
            draft: null,
          },
          options,
        );
      } else {
        navigateStandalone("/notes", { change: id }, options);
      }
    },
    [embedded, setEmbeddedSelection, navigateStandalone],
  );

  // 自分の下書き（未提出の変更提案）の通常ページ表示への遷移
  const goToDraft = useCallback(
    (id: string) => {
      if (embedded) {
        setEmbeddedSelection({
          draft: id,
          note: null,
          card: null,
          change: null,
          changes: null,
        });
      } else {
        navigateStandalone("/notes", { draft: id });
      }
    },
    [embedded, setEmbeddedSelection, navigateStandalone],
  );

  // ノート未選択状態（一覧）へ戻す（削除後など）
  const goToList = useCallback(() => {
    if (embedded) {
      setEmbeddedSelection(
        { note: null, card: null, change: null, changes: null, draft: null },
        { replace: true },
      );
    } else {
      navigate(`/notes${filterSuffix}`, { replace: true });
    }
  }, [embedded, setEmbeddedSelection, navigate, filterSuffix]);

  const {
    data: treeData,
    isLoading: treeLoading,
    error: treeError,
  } = useGetNotesTreeQuery(
    projectFilter ? { project_id: projectFilter } : undefined,
  );

  const {
    data: noteData,
    isFetching: noteFetching,
    error: noteError,
  } = useGetNoteQuery(
    { note_id: noteId ?? "" },
    { skip: !noteId || !!changeId || !!changesSectionId || !!draftChangeId },
  );

  const [createNote, { isLoading: creating }] = useCreateNoteMutation();
  const [updateNote, { isLoading: updating }] = useUpdateNoteMutation();
  const [deleteNote, { isLoading: deleting }] = useDeleteNoteMutation();
  const [createNoteChange, { isLoading: creatingChange }] =
    useCreateNoteChangeMutation();
  const [updateNoteChange, { isLoading: updatingChange }] =
    useUpdateNoteChangeMutation();
  const [deleteNoteChange, { isLoading: withdrawing }] =
    useDeleteNoteChangeMutation();

  const [editorState, setEditorState] = useState<EditorState>({
    mode: "closed",
  });
  const [pendingSubmit, setPendingSubmit] = useState<PendingSubmit | null>(
    null,
  );
  const [sectionModal, setSectionModal] = useState<
    { open: false } | { open: true; section: NoteSection | null }
  >({ open: false });
  const [importSection, setImportSection] = useState<NoteSection | null>(null);
  // 削除確認の対象（閲覧中のノート、またはツリーの行の3点リーダーから）
  const [deleteTarget, setDeleteTarget] = useState<Pick<
    NoteSummary,
    "id" | "title" | "display_style"
  > | null>(null);
  // 下書きの取り下げ確認の対象（?draft= 表示中、またはツリーの下書き行から）
  const [withdrawTarget, setWithdrawTarget] = useState<{
    id: string;
    title: string;
  } | null>(null);
  // ツリーの行の「編集」で、ページを開いたら編集モードに入る対象
  const [pendingEditNoteId, setPendingEditNoteId] = useState<string | null>(
    null,
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // エディタ内の現在の表示形式（保存ボタンのラベル出し分け用。
  // NoteEditor の onDisplayStyleChange で追従する）
  const [editorStyle, setEditorStyle] = useState<NoteContentStyle>("note");
  // PC 用サイドバー幅（境界線ドラッグで可変）
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [isDraggingDivider, setIsDraggingDivider] = useState(false);

  // 境界線のドラッグリサイズ（Preview.tsx と同じ手動 col-resize パターン）
  const handleDividerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDraggingDivider(true);
      const startX = e.clientX;
      const startWidth = sidebarWidth;

      const onMouseMove = (ev: MouseEvent) => {
        const newWidth = startWidth + (ev.clientX - startX);
        setSidebarWidth(
          Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, newWidth)),
        );
      };

      const onMouseUp = () => {
        setIsDraggingDivider(false);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [sidebarWidth],
  );

  const sections = useMemo(() => {
    const all = treeData?.data?.sections ?? [];
    if (!sectionFilter) return all;
    // ヘルプセンター等のセクション直リンク時は該当セクションのみ表示する。
    // 見つからない場合（割当解除・削除等）は全件にフォールバック
    const filtered = all.filter((s) => s.id === sectionFilter);
    return filtered.length > 0 ? filtered : all;
  }, [treeData, sectionFilter]);
  const wikilinkMap = treeData?.data?.wikilink_map ?? {};
  const canManage = treeData?.data?.can_manage ?? false;
  // 未ログイン（公開セクションの匿名閲覧）判定。`/notes` は requireAuth なしの
  // 公開ルートのため、トークンが無い場合はサインイン導線を表示する
  const isAnonymous = !getAuthTokenFromCookies();
  // 取得済みノートは URL の noteId と一致するときだけ採用する
  // （引数切替直後に残る前ノートのキャッシュを別ノートとして描かないための防御。
  // 通常は noteFetching ガードで足りるが、編集初期値の取り違えを確実に防ぐ）
  const fetchedNote = noteData?.data?.note ?? null;
  const note = fetchedNote && fetchedNote.id === noteId ? fetchedNote : null;

  const changesSection = useMemo(
    () => sections.find((s) => s.id === changesSectionId) ?? null,
    [sections, changesSectionId],
  );

  // 編集対象ノートに対する自分の下書き（あれば編集時に引き継ぐ）。
  // BE の一覧は draft を本人にのみ返すため、返ってきた draft は自分のもの。
  // target_type は指定せず、ノート/カードどちらの下書きも拾う。
  // NOTE: RTK Query の `data` は引数切替・skip 後も直前の結果を返し続けるため、
  // `currentData`（現在の引数に対する結果のみ）を使い、さらに ID を照合する。
  // これを怠ると「下書きのあるノート → 別ノート → 編集」の操作で
  // 別ノートの下書きが編集初期値に紛れ込む。
  const { currentData: draftData, isFetching: draftListFetching } =
    useGetNoteChangesQuery(
      {
        target_id: noteId ?? "",
        status: "draft",
      },
      // 未ログイン時は下書き API が 401 になるためスキップ（閲覧専用）
      {
        skip:
          isAnonymous ||
          !noteId ||
          !!changeId ||
          !!changesSectionId ||
          !!draftChangeId,
      },
    );
  const myDraftSummary = draftData?.data?.changes?.[0] ?? null;
  // payload が必要なため詳細を引く（編集開始時に初期値として使う）
  const {
    currentData: myDraftDetailData,
    isFetching: draftDetailFetching,
    isError: draftDetailError,
  } = useGetNoteChangeQuery(
    { change_id: myDraftSummary?.id ?? "" },
    { skip: !myDraftSummary },
  );
  const myDraftCandidate = myDraftDetailData?.data?.change ?? null;
  // summary と一致し、いま開いているノートを対象にした詳細だけを採用する
  const myDraft =
    myDraftSummary &&
    myDraftCandidate &&
    myDraftCandidate.id === myDraftSummary.id &&
    myDraftCandidate.target_id === noteId
      ? myDraftCandidate
      : null;
  // 下書きの解決が終わっていない間は true（一覧/詳細の取得中、または
  // summary に対応する詳細が未着）。この間に編集エディタを開くと
  // 誤った初期値のまま固定されるため、開くのを待つ。
  // 詳細がエラー（他所での取り下げ等）のときは「下書きなし」として解決させる。
  const myDraftPending =
    draftListFetching ||
    draftDetailFetching ||
    (!!myDraftSummary &&
      !draftDetailError &&
      myDraftCandidate?.id !== myDraftSummary.id);

  // 編集モードに入ったら（または開いた時点で保留中だった下書きが解決したら）、
  // 保存ボタンのラベル用の表示形式を初期値へ合わせ直す。openEditor の時点では
  // myDraft が未解決のことがあるため、確定後のここで揃える。
  // エディタ内での切替はその後 onDisplayStyleChange が上書きする。
  useEffect(() => {
    if (editorState.mode !== "edit" || myDraftPending || !note) return;
    setEditorStyle(myDraft ? myDraft.target_type : toContentStyle(note.display_style));
  }, [editorState.mode, myDraftPending, myDraft, note]);

  // ツリーの「下書き」行から開いた下書き（?draft= の通常ページ表示用）
  const {
    data: draftChangeData,
    isFetching: draftFetching,
    error: draftError,
  } = useGetNoteChangeQuery(
    { change_id: draftChangeId ?? "" },
    { skip: !draftChangeId },
  );
  const draftChange =
    draftChangeId && draftChangeData?.data?.change?.id === draftChangeId
      ? draftChangeData.data.change
      : null;

  // 提出済み（draft でなくなった）下書きは PR 詳細（?change=）へ付け替える
  // （提出直後の遷移は submitChange 側で行うため、これは直リンク等の保険）
  useEffect(() => {
    if (draftChangeId && draftChange && draftChange.status !== "draft") {
      goToChange(draftChange.id, { replace: true });
    }
  }, [draftChangeId, draftChange, goToChange]);

  // ノート未選択時は最初のノートへ（URL は置換して履歴を汚さない）。
  useEffect(() => {
    if (noteId || changeId || changesSectionId || draftChangeId || treeLoading)
      return;
    if (embedded && !isNotesTabActive) return;
    const firstNote = sections.find((s) => s.notes.length > 0)?.notes[0];
    if (firstNote) {
      goToNote(firstNote.id, { replace: true });
    }
  }, [
    noteId,
    changeId,
    changesSectionId,
    draftChangeId,
    treeLoading,
    sections,
    goToNote,
    embedded,
    isNotesTabActive,
  ]);

  // 本文エリアにイメージボードを出しているか（PR / 下書き / 新規作成の表示が優先）
  const isBoardView =
    note?.display_style === "board" &&
    !changeId &&
    !changesSectionId &&
    !draftChangeId &&
    editorState.mode !== "create";

  // ノート/PR/下書き表示を切り替えたら編集モードとモバイルサイドバーを閉じる
  useEffect(() => {
    setEditorState({ mode: "closed" });
    setMobileSidebarOpen(false);
  }, [noteId, changeId, changesSectionId, draftChangeId]);

  // ツリーの行の「編集」: 対象ページが読み込まれたら編集モードに入る
  // （上の切替時リセットより後に宣言し、同じコミットで閉じられないようにする）
  useEffect(() => {
    if (!pendingEditNoteId || !note || note.id !== pendingEditNoteId) return;
    setPendingEditNoteId(null);
    setEditorStyle(myDraft ? myDraft.target_type : toContentStyle(note.display_style));
    setEditorState({ mode: "edit" });
  }, [pendingEditNoteId, note, myDraft]);

  /**
   * 1 カラム（SP）ではサイドバーと本文が排他表示で、本文側は display: none。
   * サイドバーから本文に何かを出す操作（選択・新規作成）をしたときは
   * 一覧を閉じないと「押しても何も起きない」ように見えるため、本文へ切り替える。
   * ルートが変わる操作は選択変更の useEffect でも閉じるが、同じノートを
   * 選び直した場合はルートが変わらないためここで明示的に閉じる。
   */
  const showContentPane = useCallback(() => setMobileSidebarOpen(false), []);

  const handleSelectNote = useCallback(
    (id: string) => {
      goToNote(id);
      showContentPane();
    },
    [goToNote, showContentPane],
  );

  // ── ノートの Markdown 一括書き出し（.zip） ──────────────────────
  // セクション内（またはフォルダ内）の全ノート（カード表示含む）の本文を
  // 順次取得し、zip としてダウンロードする（インポートと対称: ファイル名 =
  // タイトル、本文 = Markdown）。本文中の S3 アップロードファイルは md と
  // 同階層の images/（画像）・attachments/（添付ファイル）に同梱し、リンクを
  // 相対パスへ書き換える（画像は ImportNotesModal の basename 一致置換で
  // 往復できる）。
  const [fetchNoteForExport] = useLazyGetNoteQuery();
  const exportingRef = useRef(false);

  const handleExportMarkdown = useCallback(
    async (section: NoteSection, folder?: NoteFolderSummary) => {
      if (exportingRef.current) return;
      // フォルダ ID → zip 内ディレクトリパス（入れ子はサブディレクトリで再現）。
      // フォルダ指定時はそのフォルダを基準（zip 直下）とした相対パスにし、
      // 子孫フォルダの中身も含める。
      const folderDirs = buildFolderDirMap(section.folders, folder);
      // フォルダ指定時はそのフォルダ（子孫を含む）内のノートのみ書き出す。
      // イメージボードは Markdown 本文を持たないため対象外
      const exportable = section.notes.filter((n) => n.display_style !== "board");
      const targetItems = folder
        ? exportable.filter(
            (n) => n.folder_id && folderDirs.has(n.folder_id),
          )
        : exportable;
      if (targetItems.length === 0) {
        toast.error("書き出せるノート/カードがありません");
        return;
      }
      exportingRef.current = true;
      const toastId = toast.loading("書き出し中...");
      try {
        // jszip は書き出し時のみ必要なため動的 import（初期バンドルを増やさない）
        const { default: JSZip } = await import("jszip");
        const zip = new JSZip();

        // ディレクトリ単位でファイル名の重複を回避する
        const usedNamesByDir = new Map<string, Set<string>>();
        // 同梱ファイルの採番状態: `${dir}\n${subdir}` → { URL → ファイル名, 採番済み }
        const assetsByDir = new Map<
          string,
          { byUrl: Map<string, string>; used: Set<string> }
        >();
        // 同一 URL のファイルは 1 回だけ取得する（失敗した URL は null を記録）
        const blobByUrl = new Map<string, Blob | null>();

        /**
         * S3 アップロードファイル 1 件を取得して zip の `{dir}/{subdir}/` に
         * 格納し、採番済みファイル名を返す（取得失敗時は null）。
         * 同一ディレクトリで取得済みの URL は同じファイルを共有する。
         */
        const addAsset = async (
          url: string,
          dir: string | null,
          subdir: "images" | "attachments",
        ): Promise<string | null> => {
          const assetKey = `${dir ?? ""}\n${subdir}`;
          let dirAssets = assetsByDir.get(assetKey);
          if (!dirAssets) {
            dirAssets = { byUrl: new Map(), used: new Set() };
            assetsByDir.set(assetKey, dirAssets);
          }
          const existing = dirAssets.byUrl.get(url);
          if (existing) return existing;
          if (!blobByUrl.has(url)) {
            try {
              blobByUrl.set(url, await fetchFileAsBlob(url));
            } catch {
              blobByUrl.set(url, null);
            }
          }
          const blob = blobByUrl.get(url);
          if (!blob) return null;
          const assetName = uniqueAssetFileName(
            fileNameFromUrl(url),
            dirAssets.used,
          );
          dirAssets.byUrl.set(url, assetName);
          zip.file(
            dir ? `${dir}/${subdir}/${assetName}` : `${subdir}/${assetName}`,
            blob,
          );
          return assetName;
        };

        /**
         * 本文中の S3 アップロードファイルをまとめて zip に格納し、
         * URL → 相対パスの置換表を返す。
         * 取得に失敗したファイルは URL のまま残す（書き出しは継続）。
         */
        const collectAssets = async (
          urls: string[],
          dir: string | null,
          subdir: "images" | "attachments",
        ): Promise<Map<string, string>> => {
          const replacements = new Map<string, string>();
          for (const url of urls) {
            const assetName = await addAsset(url, dir, subdir);
            if (assetName) replacements.set(url, `${subdir}/${assetName}`);
          }
          return replacements;
        };

        for (const item of targetItems) {
          const res = await fetchNoteForExport(
            { note_id: item.id },
            true, // preferCacheValue
          ).unwrap();
          const title =
            res.data.note.title ||
            (item.display_style === "card" ? "無題のカード" : "無題のノート");
          const rawContent = res.data.note.content ?? "";
          const dir = item.folder_id
            ? folderDirs.get(item.folder_id) ?? null
            : null;
          const dirKey = dir ?? "";
          if (!usedNamesByDir.has(dirKey)) {
            usedNamesByDir.set(dirKey, new Set());
          }
          const fileName = uniqueFileName(
            sanitizeFileName(title),
            usedNamesByDir.get(dirKey)!,
          );

          // ── 画像 / 添付ファイルの同梱とリンクの相対パス化 ──
          let content = rawContent;
          const imageUrls = [...new Set(extractImageUrls(content))].filter(
            isNoteUploadUrl,
          );
          const attachmentUrls = [
            ...new Set(extractAttachmentUrls(content)),
          ].filter(isNoteUploadUrl);
          const replacements = new Map([
            ...(await collectAssets(imageUrls, dir, "images")),
            ...(await collectAssets(attachmentUrls, dir, "attachments")),
          ]);
          if (replacements.size > 0) {
            content = rewriteFileRefs(content, replacements);
          }

          const path = dir ? `${dir}/${fileName}.md` : `${fileName}.md`;
          zip.file(path, content);
        }

        const blob = await zip.generateAsync({ type: "blob" });
        const zipName = folder
          ? sanitizeFileName(folder.title || "フォルダ")
          : sanitizeFileName(section.title || "notes");
        downloadBlob(blob, `${zipName}.zip`);
        const noteCount = targetItems.filter(
          (n) => n.display_style !== "card",
        ).length;
        const cardCount = targetItems.length - noteCount;
        const summaryText = [
          noteCount > 0 ? `ノート${noteCount}件` : null,
          cardCount > 0 ? `カード${cardCount}件` : null,
        ]
          .filter(Boolean)
          .join("・");
        toast.success(`${summaryText}を書き出しました`, { id: toastId });
      } catch {
        toast.error("書き出しに失敗しました", { id: toastId });
      } finally {
        exportingRef.current = false;
      }
    },
    [fetchNoteForExport],
  );

  // ── ノート/カードの保存（直接更新） ─────────────────────────────

  const handleSaveDirect = useCallback(
    async (payload: NoteSavePayload, resend: boolean) => {
      try {
        if (editorState.mode === "create") {
          const result = await createNote({
            section_id: editorState.section.id,
            // フォルダの3点メニューからの作成はそのフォルダ直下に置く
            ...(editorState.folder ? { folder_id: editorState.folder.id } : {}),
            ...payload,
          }).unwrap();
          toast.success(
            payload.display_style === "card"
              ? "カードを作成しました"
              : "ノートを作成しました",
          );
          setEditorState({ mode: "closed" });
          goToNote(result.data.note.id);
        } else if (editorState.mode === "edit" && note) {
          await updateNote({
            note_id: note.id,
            ...payload,
            // 「更新して再送」は既読/評価をリセットし、やることリストへ再表示
            ...(resend ? { reset_reactions: true } : {}),
          }).unwrap();
          toast.success(resend ? "更新して再送しました" : "更新しました");
          setEditorState({ mode: "closed" });
        }
      } catch {
        toast.error("保存に失敗しました");
      }
    },
    [editorState, note, createNote, updateNote, goToNote],
  );

  // ── イメージボードの作成（中身はボード上で足すので、エディタを経ずに作る） ──
  const handleCreateBoard = useCallback(
    async (section: NoteSection, folder?: NoteFolderSummary) => {
      try {
        const result = await createNote({
          section_id: section.id,
          ...(folder ? { folder_id: folder.id } : {}),
          title: "イメージボード",
          display_style: "board",
        }).unwrap();
        toast.success("イメージボードを作成しました");
        goToNote(result.data.note.id);
        showContentPane();
      } catch {
        toast.error("イメージボードの作成に失敗しました");
      }
    },
    [createNote, goToNote, showContentPane],
  );

  // ── 下書き保存 / プルリクエスト（変更提案） ─────────────────────

  const saveDraft = useCallback(
    async (
      targetType: NoteChangeTargetType,
      sectionId: string,
      payload: NoteChangePayload,
      targetId?: string,
      draftId?: string,
    ) => {
      try {
        if (draftId) {
          await updateNoteChange({ change_id: draftId, payload }).unwrap();
        } else {
          await createNoteChange({
            target_type: targetType,
            ...(targetId ? { target_id: targetId } : {}),
            section_id: sectionId,
            payload,
          }).unwrap();
        }
        toast.success("下書きを保存しました");
        setEditorState({ mode: "closed" });
      } catch {
        toast.error("下書きの保存に失敗しました");
      }
    },
    [createNoteChange, updateNoteChange],
  );

  // 承認不要フォルダに配置された下書きは、レビューなしでそのまま公開できる
  const publishDraft = useCallback(
    async (draftId: string, payload: NoteChangePayload) => {
      try {
        const result = await updateNoteChange({
          change_id: draftId,
          payload,
          publish: true,
        }).unwrap();
        toast.success("公開しました");
        setEditorState({ mode: "closed" });
        const published = result.data.target;
        if (published) goToNote(published.id);
      } catch {
        toast.error("公開できませんでした");
      }
    },
    [updateNoteChange, goToNote],
  );

  const submitChange = useCallback(
    async (description: string) => {
      if (!pendingSubmit) return;
      try {
        let resultChangeId: string;
        if (pendingSubmit.draftId) {
          const result = await updateNoteChange({
            change_id: pendingSubmit.draftId,
            payload: pendingSubmit.payload,
            description,
            submit: true,
          }).unwrap();
          resultChangeId = result.data.change.id;
        } else {
          const result = await createNoteChange({
            target_type: pendingSubmit.targetType,
            ...(pendingSubmit.targetId
              ? { target_id: pendingSubmit.targetId }
              : {}),
            section_id: pendingSubmit.sectionId,
            payload: pendingSubmit.payload,
            description,
            submit: true,
          }).unwrap();
          resultChangeId = result.data.change.id;
        }
        toast.success("プルリクエストを送信しました");
        setPendingSubmit(null);
        setEditorState({ mode: "closed" });
        goToChange(resultChangeId);
      } catch {
        toast.error("プルリクエストの送信に失敗しました");
      }
    },
    [pendingSubmit, createNoteChange, updateNoteChange, goToChange],
  );

  // ── ノートの削除 ────────────────────────────────────────────────

  const handleDeleteNote = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteNote({ note_id: deleteTarget.id }).unwrap();
      toast.success(
        deleteTarget.display_style === "card"
          ? "カードを削除しました"
          : "ノートを削除しました",
      );
      setDeleteTarget(null);
      // 閲覧中のページを消したときだけ一覧へ戻す
      if (deleteTarget.id === noteId) {
        setEditorState({ mode: "closed" });
        goToList();
      }
    } catch {
      toast.error("削除に失敗しました");
    }
  }, [deleteTarget, noteId, deleteNote, goToList]);

  // ── 下書きの取り下げ ───────────────────────────────────────────
  const handleWithdrawDraft = useCallback(async () => {
    if (!withdrawTarget) return;
    try {
      await deleteNoteChange({ change_id: withdrawTarget.id }).unwrap();
      toast.success("下書きを取り下げました");
      setWithdrawTarget(null);
      // 表示中の下書きを取り下げたときだけ一覧へ戻す
      if (withdrawTarget.id === draftChangeId) goToList();
    } catch {
      toast.error("取り下げに失敗しました");
    }
  }, [withdrawTarget, draftChangeId, deleteNoteChange, goToList]);

  // 閲覧画面からの表示形式の切替（ノート ⇄ カード。直接更新権限者のみ）
  const handleSwitchStyle = useCallback(
    async (style: NoteContentStyle) => {
      if (!note) return;
      try {
        await updateNote({ note_id: note.id, display_style: style }).unwrap();
        toast.success(
          style === "card" ? "カード表示に切り替えました" : "ノート表示に切り替えました",
        );
      } catch {
        toast.error("切り替えに失敗しました");
      }
    },
    [note, updateNote],
  );

  // チェックボックスのトグル等、閲覧画面からの本文更新（楽観的更新で即時反映）。
  // 直接更新権限がある場合のみ（PR 必須のユーザーは編集提案から変更する）。
  const handleContentChange = useCallback(
    async (newContent: string) => {
      if (!note) return;
      try {
        await updateNote({ note_id: note.id, content: newContent }).unwrap();
      } catch {
        toast.error("更新に失敗しました");
      }
    },
    [note, updateNote],
  );

  if (treeLoading) return <SvgLoadingAnimation />;

  if (treeError) {
    return (
      <EmptyState>
        <p className="bodytext-13 u-text-light-gray">
          ノートの読み込みに失敗しました
        </p>
      </EmptyState>
    );
  }

  const savingChange = creatingChange || updatingChange;

  /** 下書き編集中の案内バナー */
  const draftNotice = (visible: boolean) =>
    visible ? (
      <DraftNotice>
        <span className="material-symbols-rounded">edit_note</span>
        <span className="bodytext-11 u-line-height-150">
          保存済みの下書きを編集しています。「プルリクエスト」で送信するまで
          他のメンバーには公開されません。
        </span>
      </DraftNotice>
    ) : null;

  /** 未ログイン時のみ表示するサインイン導線（EmptyState 内で使用） */
  const signinCta = isAnonymous ? (
    <>
      <p className="bodytext-11 u-text-light-gray">
        サインインすると閲覧できるノートが増える場合があります
      </p>
      <Button size="small" onClick={() => navigate("/signin")}>
        サインイン
      </Button>
    </>
  ) : null;

  const renderContent = () => {
    // ── プルリクエスト詳細 / 一覧（最優先） ─────────────────────
    if (changeId) {
      return (
        <ChangeDetailView
          changeId={changeId}
          sections={sections}
          onBack={(sectionId) =>
            sectionId ? goToChangesList(sectionId) : goToList()
          }
          // ノート/カード統合後は対象の種類によらずノート URL で開く
          onOpenTarget={(_targetType, targetId) => goToNote(targetId)}
        />
      );
    }

    if (changesSectionId) {
      if (!changesSection) {
        return (
          <EmptyState>
            <p className="bodytext-13 u-text-light-gray">
              セクションが見つからないか、閲覧権限がありません
            </p>
          </EmptyState>
        );
      }
      return (
        <ChangesListView
          section={changesSection}
          onOpenChange={goToChange}
        />
      );
    }

    // ── ノート/カード新規作成（表示形式はエディタ内で切替可能） ──
    if (editorState.mode === "create") {
      const section = editorState.section;
      // 承認不要フォルダ（祖先を含む）の中への作成はコントリビューターも直接
      const direct =
        section.can_direct_update ||
        (section.can_contribute &&
          !!editorState.folder?.effective_skip_review);
      return (
        <NoteEditor
          initialTitle=""
          initialContent=""
          // 新規作成は常にノートから始める（エディタ内でカードへ切替可能）
          initialMeta={{ display_style: "note" }}
          styleSwitchable
          onDisplayStyleChange={setEditorStyle}
          saving={creating || savingChange}
          saveLabel={direct ? "保存" : "プルリクエスト"}
          onSave={(payload) => {
            if (direct) {
              void handleSaveDirect(payload, false);
            } else {
              // PR の payload 形は表示形式で決まる（note = title/content のみ）。
              // フォルダの3点メニューからの作成はマージ時にそのフォルダへ。
              setPendingSubmit({
                targetType: payload.display_style,
                sectionId: section.id,
                payload: {
                  ...toChangePayload(payload.display_style, payload),
                  ...(editorState.folder
                    ? { folder_id: editorState.folder.id }
                    : {}),
                },
              });
            }
          }}
          draftSaveLabel="下書き保存"
          onSaveDraft={(payload) =>
            void saveDraft(payload.display_style, section.id, {
              ...toChangePayload(payload.display_style, payload),
              ...(editorState.folder
                ? { folder_id: editorState.folder.id }
                : {}),
            })
          }
          onCancel={() => setEditorState({ mode: "closed" })}
          embedded={embedded}
        />
      );
    }

    // ── 下書き（未提出の変更提案）の通常ページ表示（?draft=） ─────
    if (draftChangeId) {
      if (draftError) {
        return (
          <EmptyState>
            <span
              className="material-symbols-rounded"
              style={{ fontSize: "48px", color: "var(--soremo-light-gray)" }}
            >
              search_off
            </span>
            <p className="bodytext-13 u-text-light-gray">
              下書きが見つからないか、すでに取り下げられています
            </p>
          </EmptyState>
        );
      }
      // 提出済みは effect で ?change= へ付け替えるまでローディングを出す
      if (!draftChange || draftFetching || draftChange.status !== "draft") {
        return <SvgLoadingAnimation />;
      }

      const payload = draftChange.payload;
      const targetType = draftChange.target_type;
      const canUpdate = draftChange.permissions.can_update;
      // 承認不要フォルダ（祖先を含む）に配置された新規作成の下書きは、
      // プルリクエストではなく「公開」でそのまま反映できる
      const draftSection = sections.find(
        (s) => s.id === draftChange.section_id,
      );
      const draftPublishable =
        draftChange.kind === "create" &&
        !!draftSection?.can_contribute &&
        !!draftSection.folders.find((f) => f.id === payload.folder_id)
          ?.effective_skip_review;

      // 編集は通常ノートと同じ埋め込みエディタ（下書き保存 / プルリクエスト）
      if (editorState.mode === "edit" && canUpdate) {
        return (
          <NoteEditor
            initialTitle={payload.title}
            initialContent={payload.content}
            initialMeta={{ ...payload, display_style: targetType }}
            saving={savingChange}
            saveLabel={draftPublishable ? "公開" : "プルリクエスト"}
            onSave={(p) => {
              if (draftPublishable) {
                void publishDraft(
                  draftChange.id,
                  toChangePayload(targetType, p),
                );
                return;
              }
              setPendingSubmit({
                targetType,
                targetId: draftChange.target_id ?? undefined,
                sectionId: draftChange.section_id,
                payload: toChangePayload(targetType, p),
                draftId: draftChange.id,
                draftDescription: draftChange.description,
              });
            }}
            draftSaveLabel="下書き保存"
            onSaveDraft={(p) =>
              void saveDraft(
                targetType,
                draftChange.section_id,
                toChangePayload(targetType, p),
                draftChange.target_id ?? undefined,
                draftChange.id,
              )
            }
            headerFields={draftNotice(true)}
            onCancel={() => setEditorState({ mode: "closed" })}
            embedded={embedded}
          />
        );
      }

      // payload から NoteDetail 相当を合成して通常ノートと同じビューで表示
      // （権限フィールドはビュー側では参照されないためダミー値でよい）
      const draftDetail: NoteDetail = {
        id: draftChange.id,
        section_id: draftChange.section_id,
        folder_id: payload.folder_id ?? null,
        title: payload.title,
        content: payload.content,
        display_style: targetType,
        category: payload.category ?? "",
        expires_at: payload.expires_at ?? null,
        is_required: !!payload.is_required,
        is_published: !!payload.is_published,
        sort_order: 0,
        created: draftChange.created,
        modified: draftChange.modified,
        created_by: draftChange.author,
        updated_by: draftChange.author,
        my_role: "contributor",
        can_edit: false,
        can_contribute: true,
        can_direct_update: false,
      };
      const openDraftEditor = () => {
        setEditorStyle(targetType);
        setEditorState({ mode: "edit" });
      };
      const requestWithdraw = () =>
        setWithdrawTarget({ id: draftChange.id, title: draftChange.title });
      if (targetType === "card") {
        return (
          <NoteCardView
            card={draftDetail}
            draftMode
            deleteLabel="取り下げ"
            onEdit={canUpdate ? openDraftEditor : undefined}
            onDelete={canUpdate ? requestWithdraw : undefined}
            projectId={embedded ? projectId : undefined}
          />
        );
      }
      return (
        <NoteView
          note={draftDetail}
          wikilinkMap={wikilinkMap}
          onNavigateToNote={handleSelectNote}
          draftMode
          deleteLabel="取り下げ"
          onEdit={canUpdate ? openDraftEditor : undefined}
          onDelete={canUpdate ? requestWithdraw : undefined}
          projectId={embedded ? projectId : undefined}
        />
      );
    }

    if (noteId && noteError) {
      return (
        <EmptyState>
          <span
            className="material-symbols-rounded"
            style={{ fontSize: "48px", color: "var(--soremo-light-gray)" }}
          >
            search_off
          </span>
          <p className="bodytext-13 u-text-light-gray">
            ノートが見つからないか、閲覧権限がありません
          </p>
          {signinCta}
        </EmptyState>
      );
    }

    if (noteId && (noteFetching || !note)) {
      return <SvgLoadingAnimation />;
    }

    // ── イメージボード（中身は画像と手描き。エディタは持たない） ──
    if (isBoardView && note) {
      return (
        <ImageBoard
          key={note.id}
          noteId={note.id}
          sectionId={note.section_id}
          title={note.title}
          linkProjectId={embedded ? projectId : undefined}
          readOnly={note.my_role === "public"}
        />
      );
    }

    // ── ノート/カード編集（統合エディタ） ───────────────────────
    if (note && editorState.mode === "edit") {
      // 自分の下書きの解決を待ってからエディタを開く（NoteEditor は初期値を
      // マウント時にしか読まないため、確定前に開くと誤った内容で固定される）
      if (myDraftPending) {
        return <SvgLoadingAnimation />;
      }
      const direct = note.can_direct_update;
      const noteDraft = myDraft;
      // ボードはこの分岐に来ない（上で ImageBoard を返す）
      const noteStyle = toContentStyle(note.display_style);
      // 下書きがあればその payload（target_type で形が決まる）を初期値にする
      const initialStyle: NoteContentStyle = noteDraft
        ? noteDraft.target_type
        : noteStyle;
      // カード表示の直接更新は「更新して再送」（既読リセット）が主アクション
      const editingCard = editorStyle === "card";
      return (
        <NoteEditor
          // ノート・下書きが切り替わったら必ず作り直す（初期値の持ち越し防止）
          key={`${note.id}:${noteDraft?.id ?? "no-draft"}`}
          initialTitle={noteDraft?.payload.title ?? note.title}
          initialContent={noteDraft?.payload.content ?? note.content}
          initialMeta={
            noteDraft
              ? { ...noteDraft.payload, display_style: initialStyle }
              : {
                  display_style: noteStyle,
                  category: note.category,
                  expires_at: note.expires_at,
                  is_required: note.is_required,
                  is_published: note.is_published,
                }
          }
          // 表示形式の切替は直接更新のみ（PR の payload 形は target_type 固定）
          styleSwitchable={direct}
          onDisplayStyleChange={setEditorStyle}
          saving={updating || savingChange}
          saveLabel={
            direct
              ? editingCard
                ? "更新して再送"
                : "更新"
              : "プルリクエスト"
          }
          onSave={(payload) => {
            if (direct) {
              // カード表示の主アクションは既読/評価をリセットして再送
              void handleSaveDirect(payload, payload.display_style === "card");
            } else {
              const targetType = noteDraft?.target_type ?? noteStyle;
              setPendingSubmit({
                targetType,
                targetId: note.id,
                sectionId: note.section_id,
                payload: toChangePayload(targetType, payload),
                draftId: noteDraft?.id,
                draftDescription: noteDraft?.description,
              });
            }
          }}
          middleSaveLabel={direct && editingCard ? "更新のみ" : undefined}
          onSaveMiddle={
            direct && editingCard
              ? (payload) => void handleSaveDirect(payload, false)
              : undefined
          }
          draftSaveLabel="下書き保存"
          onSaveDraft={(payload) => {
            const targetType = noteDraft?.target_type ?? payload.display_style;
            void saveDraft(
              targetType,
              note.section_id,
              toChangePayload(targetType, payload),
              note.id,
              noteDraft?.id,
            );
          }}
          headerFields={draftNotice(!!noteDraft)}
          onCancel={() => setEditorState({ mode: "closed" })}
          embedded={embedded}
        />
      );
    }

    if (note) {
      // 編集開始時にエディタの表示形式ラベルを初期化する
      // （下書きがあれば下書きの形式に合わせる）
      const openEditor = () => {
        setEditorStyle(myDraft ? myDraft.target_type : toContentStyle(note.display_style));
        setEditorState({ mode: "edit" });
      };
      // 表示形式で閲覧ビューを出し分ける（同じノートをカード/ノートで切替可能）
      if (note.display_style === "card") {
        return (
          <NoteCardView
            card={note}
            onEdit={note.can_contribute ? openEditor : undefined}
            onDelete={
              note.can_edit ? () => setDeleteTarget(note) : undefined
            }
            // 表示形式の切替は削除等と同じくメンテナー以上の直接操作
            onSwitchStyle={
              note.can_edit ? () => void handleSwitchStyle("note") : undefined
            }
            projectId={embedded ? projectId : undefined}
          />
        );
      }
      return (
        <NoteView
          note={note}
          wikilinkMap={wikilinkMap}
          onNavigateToNote={handleSelectNote}
          onEdit={note.can_contribute ? openEditor : undefined}
          onDelete={note.can_edit ? () => setDeleteTarget(note) : undefined}
          onContentChange={
            note.can_direct_update ? handleContentChange : undefined
          }
          // 表示形式の切替は削除等と同じくメンテナー以上の直接操作
          onSwitchStyle={
            note.can_edit ? () => void handleSwitchStyle("card") : undefined
          }
          projectId={embedded ? projectId : undefined}
        />
      );
    }

    return (
      <EmptyState>
        <span
          className="material-symbols-rounded"
          style={{ fontSize: "48px", color: "var(--soremo-light-gray)" }}
        >
          import_contacts
        </span>
        <p className="bodytext-13 u-text-light-gray">
          {(() => {
            if (sections.length === 0) {
              if (isAnonymous) return "公開されているノートはありません";
              return canManage
                ? "「セクションを追加」からノートスペースを作成できます"
                : "閲覧できるノートはまだありません";
            }
            const hasAnyNote = sections.some((s) => s.notes.length > 0);
            if (hasAnyNote) {
              return "左のツリーからノートを選択してください";
            }
            // セクションはあるがノートが 1 件もない場合の案内（権限に応じて）
            return sections.some((s) => s.can_contribute)
              ? "「ノートを追加」から最初のノートを作成できます"
              : "閲覧できるノートはまだありません";
          })()}
        </p>
        {sections.length === 0 && signinCta}
      </EmptyState>
    );
  };

  return (
    <PageWrapper
      $embedded={embedded}
      $singleColumn={!isTwoColumn}
      $scrollDown={scrollState === "down"}
    >
      {!isTwoColumn && (
        <MobileToolbar>
          <MobileMenuButton
            onClick={() => setMobileSidebarOpen((v) => !v)}
            aria-label="ノート一覧を開閉"
          >
            <span className="material-symbols-rounded">
              {mobileSidebarOpen ? "left_panel_close" : "left_panel_open"}
            </span>
            <span className="bodytext-13">ノート一覧</span>
          </MobileMenuButton>
        </MobileToolbar>
      )}

      {(() => {
        const sidebar = (
          <NotesSidebar
            sections={sections}
            selectedNoteId={
              changeId || changesSectionId || draftChangeId
                ? null
                : (noteId ?? null)
            }
            selectedDraftId={draftChangeId ?? null}
            selectedChangesSectionId={changesSectionId ?? null}
            canManage={canManage}
            // プロジェクト絞り込みで表示セクションがそのプロジェクトひとつ
            // だけの場合、上のプロジェクトバナーと重複するため見出し
            // （バナー行）は出さない
            hideSectionHeader={!!projectFilter && sections.length === 1}
            // グローバルメニューの「共有ノート」で複数セクションが並ぶときは、
            // まだ開閉していないセクションを閉じて一覧性を優先する
            defaultSectionsCollapsed={!embedded && sections.length > 1}
            onSelectNote={handleSelectNote}
            onSelectDraft={(id) => {
              goToDraft(id);
              showContentPane();
            }}
            onOpenChanges={(section) => {
              goToChangesList(section.id);
              showContentPane();
            }}
            onCreateNote={(section, folder) => {
              setEditorState({ mode: "create", section, folder });
              setEditorStyle("note");
              showContentPane();
            }}
            onCreateBoard={(section, folder) =>
              void handleCreateBoard(section, folder)
            }
            onImportNotes={(section) => setImportSection(section)}
            onExportSection={(section) => void handleExportMarkdown(section)}
            onExportFolder={(section, folder) =>
              void handleExportMarkdown(section, folder)
            }
            onCreateSection={() =>
              setSectionModal({ open: true, section: null })
            }
            onOpenSectionSettings={(section) =>
              setSectionModal({ open: true, section })
            }
            linkProjectId={embedded ? projectId : undefined}
            onEditNote={(id) => {
              setPendingEditNoteId(id);
              const viewingThisNote =
                id === noteId &&
                !changeId &&
                !changesSectionId &&
                !draftChangeId;
              if (!viewingThisNote) goToNote(id);
              showContentPane();
            }}
            onDeleteNote={(target) => setDeleteTarget(target)}
            onWithdrawDraft={(draft) =>
              setWithdrawTarget({ id: draft.id, title: draft.title })
            }
          />
        );
        if (!isTwoColumn) {
          // 1 カラム: ツールバーのトグルでサイドバー（一覧）と本文を排他表示
          return mobileSidebarOpen ? (
            <SingleColumnSidebarPane>{sidebar}</SingleColumnSidebarPane>
          ) : null;
        }
        // 2 カラム: 境界線ドラッグで幅を変更（行は常にサイドバー幅いっぱいに追従）
        return (
          <>
            <SidebarPane $width={sidebarWidth}>{sidebar}</SidebarPane>
            <ResizeDivider
              $isDragging={isDraggingDivider}
              onMouseDown={handleDividerMouseDown}
            />
          </>
        );
      })()}

      <ContentArea
        $padTop={embedded && isTwoColumn}
        $hidden={!isTwoColumn && mobileSidebarOpen}
        $board={isBoardView}
        {...contentScrollSync}
      >
        {renderContent()}
      </ContentArea>

      {sectionModal.open && (
        <SectionSettingsModal
          section={sectionModal.section}
          canManage={canManage}
          onClose={() => setSectionModal({ open: false })}
        />
      )}

      {importSection && (
        <ImportNotesModal
          section={importSection}
          onClose={() => setImportSection(null)}
        />
      )}

      {withdrawTarget && (
        <ConfirmModal
          message={`下書き「${withdrawTarget.title}」を取り下げますか？内容は破棄されます。`}
          confirmLabel="取り下げ"
          loading={withdrawing}
          onConfirm={() => void handleWithdrawDraft()}
          onCancel={() => setWithdrawTarget(null)}
        />
      )}

      {pendingSubmit && (
        <SubmitChangeModal
          title={pendingSubmit.payload.title}
          loading={savingChange}
          initialDescription={pendingSubmit.draftDescription}
          onSubmit={(description) => void submitChange(description)}
          onClose={() => setPendingSubmit(null)}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          message={
            deleteTarget.display_style === "card"
              ? `カード「${deleteTarget.title}」を削除しますか？`
              : `「${deleteTarget.title}」を削除しますか？`
          }
          confirmLabel="削除"
          loading={deleting}
          onConfirm={handleDeleteNote}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </PageWrapper>
  );
};

export default Notes;
